import { FC, useState } from 'react';
import {
  Card,
  Icon,
  Spacing,
  Typography,
  TextField,
  Button,
  StyledDiv,
  Select,
  Dialog,
} from '../../..';
import styled from '@emotion/styled';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { FlnCategory } from '@taleemabad/db';

// Types
export interface StudentResult {
  id?: number;
  name?: string;
  uuid?: string;
  obtainedMarks: number | null;
  totalMarks: number;
  studentCategory: string | null;
}

export interface StudentCategory {
  value: string;
  label: string;
}

// Styled Components
const StudentCard = styled(Card)`
  margin-bottom: 12px;
  min-height: 130px;
`;

const CategoryBadge = styled.div<{ category: FlnCategory | null }>`
  padding: var(--rk-spacing-sm) var(--rk-spacing-md);
  border-radius: var(--rk-spacing-xl);
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: lowercase;
  white-space: nowrap;

  ${mq(BreakpointSizes.xs)} {
    padding: var(--rk-spacing-xs) var(--rk-spacing-md);
    font-size: 0.875rem;
  }

  ${({ category }) => {
    switch (category) {
      case FlnCategory.BEGINNER:
        return `
          background-color: #FFE5E5;
          color: #D32F2F;
        `;
      case FlnCategory.INTERMEDIATE:
        return `
          background-color: #FFF8E1;
          color: #F57C00;
        `;
      case FlnCategory.ADVANCED:
        return `
          background-color: #E8F5E8;
          color: #388E3C;
        `;
      case FlnCategory.GRADUATE:
        return `
          background-color: #F3E5F5;
          color: #7B1FA2;
        `;
      default:
        return `
          background-color: var(--md-sys-color-surface-variant);
          color: var(--md-sys-color-on-surface-variant);
        `;
    }
  }}
`;

const ProgressSection = styled.div`
  margin-bottom: 8px;
`;

const ProgressBar = styled.div<{ percentage: number }>`
  width: 100%;
  height: 8px;
  background-color: var(--md-sys-color-surface-variant);
  border-radius: 4px;
  overflow: hidden;
  margin: 8px 0;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${(props) => props.percentage}%;
    background-color: ${(props) => {
      if (props.percentage >= 80) return '#4CAF50';
      if (props.percentage >= 60) return '#2196F3';
      if (props.percentage >= 40) return '#FF9800';
      return '#F44336';
    }};
    transition: width 0.3s ease;
  }
`;

const EditModeContainer = styled.div`
  background-color: var(--md-sys-color-surface);
`;

const EditStudentRow = styled.div`
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid var(--md-sys-color-outline-variant);
`;

const EditFieldsContainer = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: var(--rk-spacing-lg);
  ${mq(BreakpointSizes.xs)} {
    padding: var(--rk-spacing-lg) var(--rk-spacing-sm);
    display: flex;
    flex-direction: column;
  }
`;

// Template Props Interface
export interface ReportCardManualResultTemplateProps {
  // Data
  students: StudentResult[];
  totalMarks: string;
  loading: boolean;
  isEditMode: boolean;
  isEditingTotalMarks: boolean;
  assessmentId?: string; // New prop to determine what to show

  // Event Handlers
  onBackClick: () => void;
  onTotalMarksChange: (value: string) => void;
  onSaveTotalMarks: () => void;
  onCancelTotalMarksEdit: () => void;
  onEditTotalMarks: () => void;
  onEditModeToggle: () => void;
  onStudentMarksChange: (studentId: string, value: string) => void;
  onStudentCategoryChange: (studentId: string, option: any) => void;
  onSaveChanges: () => void;
  onCancelEdit: () => void;

  // Add Student functionality
  onAddStudentClick?: (studentData: {
    firstName: string;
    lastName: string;
  }) => void;

  // Category Options
  categoryOptions: StudentCategory[];

  // Utility Functions
  formatCategoryName: (category: string | null) => string;
  getPercentage: (obtained: number, total: number) => number;
}

export const ReportCardManualResultTemplate: FC<
  ReportCardManualResultTemplateProps
> = ({
  students,
  totalMarks,
  loading,
  isEditMode,
  isEditingTotalMarks,
  assessmentId,
  onBackClick,
  onTotalMarksChange,
  onSaveTotalMarks,
  onCancelTotalMarksEdit,
  onEditTotalMarks,
  onEditModeToggle,
  onStudentMarksChange,
  onStudentCategoryChange,
  onSaveChanges,
  onCancelEdit,
  onAddStudentClick,
  categoryOptions,
  formatCategoryName,
  getPercentage,
}) => {
  // Determine what to show based on assessmentId
  const showOnlyCategory = assessmentId === '33'; //for fln assessment
  const showOnlyMarks = !showOnlyCategory;

  // Add Student popup state
  const [showAddStudentPopup, setShowAddStudentPopup] =
    useState<boolean>(false);
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [firstNameError, setFirstNameError] = useState<string>('');

  // Handlers for Add Student popup
  const handleAddStudentClick = () => {
    setShowAddStudentPopup(true);
  };

  const handlePopupClose = () => {
    setShowAddStudentPopup(false);
    clearFields();
  };

  const clearFields = () => {
    setFirstName('');
    setLastName('');
    setFirstNameError('');
  };

  const handleNameValidation = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'firstName') {
      setFirstName(value);
    } else if (name === 'lastName') {
      setLastName(value);
    }

    const trimmedFirstName =
      name === 'firstName' ? value.trim() : firstName.trim();
    const trimmedLastName =
      name === 'lastName' ? value.trim() : lastName.trim();
    const newFullName = [trimmedFirstName, trimmedLastName]
      .filter(Boolean)
      .join(' ');

    if (
      students?.some(
        (student) => student.name?.toLowerCase() === newFullName.toLowerCase(),
      )
    ) {
      setFirstNameError('The student already exists in the list');
      return;
    }
    setFirstNameError('');
  };

  const handleConfirmAddStudent = () => {
    if (onAddStudentClick && firstName.trim()) {
      onAddStudentClick({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
    }
    setShowAddStudentPopup(false);
    clearFields();
  };

  if (loading) {
    return (
      <Spacing padding="md">
        <StyledDiv flexDirection="column" alignItem="center" padding="xl">
          <Typography variant="textBodyMedium" color="textSecondary">
            Loading student data...
          </Typography>
        </StyledDiv>
      </Spacing>
    );
  }

  if (!loading && students.length === 0) {
    return (
      <Spacing padding="md">
        <StyledDiv flexDirection="column" alignItem="center" padding="xl">
          <Icon name="group" size="large" color="primary" />
          <Spacing padding="sm" />
          <Typography variant="textTitleMedium">No Students Found</Typography>
          <Typography variant="textBodyMedium" color="textSecondary">
            No students are enrolled in this class.
          </Typography>
        </StyledDiv>
      </Spacing>
    );
  }

  return (
    <Spacing padding="md">
      <StyledDiv
        flexDirection="row"
        gap={8}
        alignItem="center"
        marginBottom={16}
      >
        <Icon name="group" size="normal" color="primary" />
        <Typography variant="textTitleMedium">
          {students.length} Students
        </Typography>
      </StyledDiv>

      {/* Total Marks Section - Only in Edit Mode and when showing marks */}
      {isEditMode && showOnlyMarks && (
        <StyledDiv
          flexDirection="column"
          background="var(--md-sys-color-surface-container)"
          padding="16px"
          borderRadius="12px"
          marginBottom={16}
        >
          <Typography variant="textLabelMedium" color="textSecondary">
            Total Marks
          </Typography>
          <Spacing padding="xs" />
          {isEditingTotalMarks ? (
            <StyledDiv flexDirection="row" gap={12} alignItem="center">
              <TextField
                value={totalMarks}
                onInput={(e) => onTotalMarksChange(e.target.value)}
                placeholder="Enter total marks"
                type="number"
                width="100%"
              />
              <Button variant="filled" onClick={onSaveTotalMarks}>
                Save
              </Button>
              <Button variant="text" onClick={onCancelTotalMarksEdit}>
                Cancel
              </Button>
            </StyledDiv>
          ) : (
            <StyledDiv flexDirection="row" gap={12} alignItem="center">
              <Typography variant="textHeadlineMedium">{totalMarks}</Typography>
              <Button variant="outlined" onClick={onEditTotalMarks}>
                Edit
              </Button>
            </StyledDiv>
          )}
        </StyledDiv>
      )}

      {/* View Mode */}
      {!isEditMode ? (
        <>
          {students.map((student) => (
            <StudentCard key={student.id} variant="filled">
              <StyledDiv padding="md" flexDirection="column">
                <StyledDiv alignItem="center" gap={12} marginBottom={16}>
                  <Typography variant="textTitleMedium">
                    {student.name}
                  </Typography>

                  {/* Show category badge only in category mode */}
                  {showOnlyCategory && (
                    <CategoryBadge
                      category={student.studentCategory as FlnCategory | null}
                    >
                      {formatCategoryName(student.studentCategory)}
                    </CategoryBadge>
                  )}
                </StyledDiv>

                {/* Conditionally show marks section based on assessmentId */}
                {showOnlyMarks && (
                  <ProgressSection>
                    <StyledDiv
                      flexDirection="row"
                      justifyContent="space-between"
                      alignItem="center"
                      marginBottom={4}
                    >
                      <Typography
                        variant="textBodyMedium"
                        color="textSecondary"
                      >
                        Marks Obtained
                      </Typography>
                      <Typography variant="textLabelLarge">
                        {student.obtainedMarks || 0} / {student.totalMarks}
                      </Typography>
                    </StyledDiv>
                    <ProgressBar
                      percentage={getPercentage(
                        student.obtainedMarks || 0,
                        student.totalMarks,
                      )}
                    />
                    <Typography variant="textBodySmall" color="textSecondary">
                      {getPercentage(
                        student.obtainedMarks || 0,
                        student.totalMarks,
                      ).toFixed(1)}
                      %
                    </Typography>
                  </ProgressSection>
                )}

                {/* Show category details when only category mode */}
                {showOnlyCategory && (
                  <StyledDiv padding="sm" marginTop={8}>
                    <Typography variant="textBodyMedium" color="textSecondary">
                      Student Category:{' '}
                      <Typography variant="textLabelLarge">
                        {formatCategoryName(student.studentCategory)}
                      </Typography>
                    </Typography>
                  </StyledDiv>
                )}
              </StyledDiv>
            </StudentCard>
          ))}

          <Spacing padding="lg" />
          <StyledDiv flexDirection="column" gap={12}>
            <Button
              variant="filled"
              onClick={onEditModeToggle}
              width="100%"
              icon="edit"
            >
              {showOnlyCategory
                ? 'Edit Categories'
                : showOnlyMarks
                  ? 'Edit Marks & Categories'
                  : 'Edit Results'}
            </Button>

            <Button
              variant="outlined"
              onClick={handleAddStudentClick}
              width="100%"
              icon="add"
              iconColor="--md-sys-color-primary"
            >
              Add Student
            </Button>
          </StyledDiv>
        </>
      ) : (
        /* Edit Mode */
        <EditModeContainer>
          {students.map((student) => (
            <EditStudentRow key={student.id}>
              <StyledDiv flexDirection="row" alignItem="center" gap={12}>
                <Typography variant="textTitleMedium">
                  Name: {student.name}
                </Typography>
              </StyledDiv>
              <EditFieldsContainer>
                {/* Show marks field only when in marks mode */}
                {showOnlyMarks && (
                  <div>
                    <Typography variant="textBodySmall" color="textSecondary">
                      Obtained Marks out of {student.totalMarks}
                    </Typography>
                    <TextField
                      value={(student.obtainedMarks || 0).toString()}
                      onInput={(e) =>
                        onStudentMarksChange(
                          student.id?.toString() || '',
                          e.target.value,
                        )
                      }
                      type="number"
                      placeholder="0"
                      width="100%"
                    />
                  </div>
                )}

                {/* Show category field only when in category mode */}
                {showOnlyCategory && (
                  <div>
                    <Typography variant="textBodySmall" color="textSecondary">
                      Category
                    </Typography>
                    <Select
                      defaultValue={student.studentCategory || ''}
                      onChange={(option) =>
                        onStudentCategoryChange(
                          student.id?.toString() || '',
                          option,
                        )
                      }
                      options={categoryOptions.map((cat) => ({
                        value: cat.value,
                        content: cat.label,
                      }))}
                      label=""
                      width="100%"
                    />
                  </div>
                )}
              </EditFieldsContainer>
            </EditStudentRow>
          ))}
          <StyledDiv flexDirection="column" marginTop={12} gap={12}>
            <Button
              variant="filled"
              onClick={onSaveChanges}
              disabled={loading}
              width="100%"
              icon="save"
            >
              {loading ? 'Saving...' : 'Save Changess'}
            </Button>

            <Button
              variant="outlined"
              onClick={onCancelEdit}
              disabled={loading}
              width="100%"
              icon="arrow_back"
              iconColor="primary"
            >
              Cancel
            </Button>
          </StyledDiv>
        </EditModeContainer>
      )}

      {/* Add Student Dialog */}
      <Dialog open={showAddStudentPopup} onClose={handlePopupClose}>
        <StyledDiv flexDirection="column" alignItem="center" gap={8}>
          <Icon name="add" color="--md-sys-light-primary" />
          <Typography variant="textHeadlineSmall">Add Student</Typography>
          <TextField
            id="firstName"
            name="firstName"
            variant="outlined"
            label="First Name"
            value={firstName}
            error={!!firstNameError}
            errorText={firstNameError}
            onInput={handleNameValidation}
          />
          <TextField
            id="lastName"
            name="lastName"
            variant="outlined"
            label="Last Name"
            value={lastName}
            onInput={handleNameValidation}
          />
        </StyledDiv>
        <StyledDiv justifyContent="flex-end">
          <Button variant="text" onClick={handlePopupClose}>
            Cancel
          </Button>
          <Button
            disabled={!firstName || !!firstNameError}
            variant="filled"
            onClick={handleConfirmAddStudent}
          >
            Add Student
          </Button>
        </StyledDiv>
      </Dialog>
    </Spacing>
  );
};
